// @ts-nocheck
/*
 * Port of components/partials/dashboard/release-center/releaseUtils.js (154 lines).
 * BYTE-IDENTICAL — palette/icon meta, filter chips, sanitiser, date formatters,
 * month-group builder, and `applyReleaseFilters`.
 */
export const RELEASE_TYPE_META: any = {
  Major: { color: '#7c3aed', bg: 'rgba(124,58,237,0.12)', icon: '🚀' },
  Minor: { color: '#2563eb', bg: 'rgba(37,99,235,0.12)', icon: '✨' },
  Patch: { color: '#0891b2', bg: 'rgba(8,145,178,0.12)', icon: '🔧' },
  Hotfix: { color: '#ea580c', bg: 'rgba(234,88,12,0.12)', icon: '🔥' },
  Security: { color: '#dc2626', bg: 'rgba(220,38,38,0.12)', icon: '🛡️' },
};

export const getTypeMeta = (type: any) =>
  RELEASE_TYPE_META[type] || {
    color: '#64748b',
    bg: 'rgba(100,116,139,0.12)',
    icon: '📦',
  };

export const HIGHLIGHT_CATEGORY_META: any = {
  Added: { color: '#16a34a', icon: '＋', label: 'Added' },
  Improved: { color: '#2563eb', icon: '↑', label: 'Improved' },
  Fixed: { color: '#d97706', icon: '✓', label: 'Fixed' },
  Removed: { color: '#dc2626', icon: '－', label: 'Removed' },
};

export const getCategoryMeta = (cat: any) =>
  HIGHLIGHT_CATEGORY_META[cat] || HIGHLIGHT_CATEGORY_META.Improved;

export const FILTER_CHIPS = [
  { key: 'all', label: 'All', kind: 'all' },
  { key: 'Major', label: 'Major', kind: 'type' },
  { key: 'Minor', label: 'Minor', kind: 'type' },
  { key: 'Patch', label: 'Patch', kind: 'type' },
  { key: 'Security', label: 'Security', kind: 'type' },
  { key: 'performance', label: 'Performance', kind: 'tag' },
  { key: 'voice', label: 'Voice', kind: 'tag' },
  { key: 'crm', label: 'CRM', kind: 'tag' },
  { key: 'api', label: 'API', kind: 'tag' },
  { key: 'analytics', label: 'Analytics', kind: 'tag' },
  { key: 'whatsapp', label: 'WhatsApp', kind: 'tag' },
  { key: 'ui', label: 'UI', kind: 'tag' },
];

export const sanitizeReleaseHtml = (html: any) => {
  if (!html || typeof window === 'undefined' || !window.DOMParser) {
    return html || '';
  }
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    doc
      .querySelectorAll('script, style, iframe[srcdoc]')
      .forEach((el: any) => el.remove());
    doc.querySelectorAll('*').forEach((el: any) => {
      [...el.attributes].forEach((attr: any) => {
        const name = attr.name.toLowerCase();
        const val = (attr.value || '').trim().toLowerCase();
        if (name.startsWith('on')) el.removeAttribute(attr.name);
        if (
          (name === 'href' || name === 'src') &&
          val.startsWith('javascript:')
        ) {
          el.removeAttribute(attr.name);
        }
      });
    });
    return doc.body.innerHTML;
  } catch {
    return html;
  }
};

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const safeDate = (value: any) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

// "12 Mar 2026"
export const formatReleaseDate = (value: any) => {
  const d = safeDate(value);
  if (!d) return '—';
  return `${d.getDate()} ${MONTHS[d.getMonth()].slice(0, 3)} ${d.getFullYear()}`;
};

// "12 Mar 2026, 14:30" — used for edit/audit timestamps.
export const formatDateTime = (value: any) => {
  const d = safeDate(value);
  if (!d) return '—';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${d.getDate()} ${MONTHS[d.getMonth()].slice(0, 3)} ${d.getFullYear()}, ${hh}:${mm}`;
};

// "March 2026" — used as the timeline group heading.
export const formatMonthGroup = (value: any) => {
  const d = safeDate(value);
  if (!d) return 'Undated';
  return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

// Big day-of-month + short month, for the timeline's dominant date block.
export const splitDateParts = (value: any) => {
  const d = safeDate(value);
  if (!d) return { day: '—', month: '', year: '' };
  return {
    day: String(d.getDate()).padStart(2, '0'),
    month: MONTHS[d.getMonth()].slice(0, 3).toUpperCase(),
    year: String(d.getFullYear()),
  };
};

export const groupReleasesByMonth = (releases: any[] = []) => {
  const groups: any[] = [];
  const index = new Map();
  releases.forEach((r: any) => {
    const key = formatMonthGroup(r.release_date);
    if (!index.has(key)) {
      index.set(key, groups.length);
      groups.push({ key, releases: [] });
    }
    groups[index.get(key)].releases.push(r);
  });
  return groups;
};

export const applyReleaseFilters = (
  releases: any[],
  search: any,
  activeChip: any,
) => {
  let out = releases;

  if (activeChip && activeChip.kind === 'type') {
    out = out.filter((r: any) => r.release_type === activeChip.key);
  } else if (activeChip && activeChip.kind === 'tag') {
    out = out.filter((r: any) =>
      (r.tags || []).some((t: any) => t.slug === activeChip.key),
    );
  }

  const q = String(search || '').trim().toLowerCase();
  if (q) {
    out = out.filter((r: any) => {
      const haystack = [
        r.version,
        r.title,
        r.summary,
        r.quick_summary,
        r.content_html,
        ...(r.tags || []).map((t: any) => t.name),
        ...(r.highlights || []).map((h: any) => h.label),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }

  return out;
};
